/**
 * UI-тексты страницы города (CityPageContent).
 * Только специфика страницы города; общие надзаголовки/якоря/теги —
 * в servicePage (popularQuestions, tag.faq, tag.blog, allArticles).
 * Интерполируемые строки — функции; склонения/формат остаются в компоненте.
 */
export const cityPageUi = {
    en: {
        // Hero facts mosaic
        season: { yearRound: 'Year-round' },
        months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
        facts: { region: 'Region', whenToGo: 'When to go', airport: 'Airport', localTime: 'Local time', km: 'km' },
        // Missing-page state
        missing: { title: 'City page not found', back: 'Back to tours' },
        // Crumbs
        crumbCities: 'Cities',
        heroStay: (city) => `Find a stay in ${city}`,
        heroTours: 'Choose a tour',
        // Anchors
        anchor: { overview: 'Overview', see: 'See', map: 'Map', services: 'Services', hotels: 'Hotels', tours: 'Tours', faq: 'FAQ', articles: 'Articles', nearby: 'Nearby' },
        // Services in city
        services: {
            esim: { ix: '// connectivity', title: 'eSIM', desc: 'Roaming-free internet on arrival.', go: 'Get' },
            transport: { ix: '// transport', title: 'Transport', desc: 'Airport transfers and trips.', go: 'Find' },
            stay: { ix: '// stay', title: 'Stay', desc: 'Hotels and apartments in town.', go: 'Browse' },
            tours: { ix: '// experiences', title: 'Tours', desc: 'Excursions and boat trips.', go: 'Pick' },
        },
        // Overview section
        overviewEyebrow: (city) => `${city}. Overview`,
        overviewTitle: 'About the city',
        // What to see section
        seeEyebrow: (city) => `${city}. What to see`,
        seeTitle: 'Top sights',
        seeAllLabel: 'All sights',
        seeLead: 'What you should not miss in the city and around it.',
        // Map section
        mapEyebrow: (city) => `${city}. Map`,
        mapTitle: 'On the map',
        mapLead: 'Key sights and surroundings — to plan your walk and gauge distances.',
        // Services section
        servicesEyebrow: (city) => `${city}. Services`,
        servicesTitle: 'Plan your trip',
        servicesLead: 'Everything you need — connectivity, transport, stay and tours.',
        // Tours showcase (fallback) section
        toursEyebrow: (city) => `${city}. Tours`,
        toursTitle: 'Tours & activities',
        toursAllLabel: 'All tours',
        toursLead: 'Boat trips, canyons and walking routes you can take from the city.',
        tourTag: 'Tour',
        tourView: 'View',
        // FAQ section
        faqEyebrow: (city) => `${city}. Questions & answers`,
        faqLead: 'Answers about seasonality, routes, neighbourhoods and basic trip planning.',
        // Articles section
        articlesEyebrow: (city) => `${city}. Helpful resources`,
        articlesTitle: 'Articles',
        // Nearby section
        nearbyEyebrow: (city) => `${city}. Nearby`,
        nearbyTitleAll: 'All cities',
        nearbyTitle: 'Nearby cities',
        nearbyAllLabel: 'All cities',
        nearbyLeadAll: 'Every city in the country — pick your next destination.',
        nearbyLead: 'Towns nearby — easy to add to your route for a day trip.',
        loading: 'Loading…',
        nearbyOnly: 'Nearby only',
        showAll: 'Show all cities',
        // Bottom CTA
        ctaEyebrow: 'Plan your trip in minutes',
        ctaTitle: (city) => `${city} — everything you need for your trip.`,
        ctaConnectivity: 'Connectivity →',
        ctaStay: 'Stay →',
        ctaTours: 'Tours →',
    },
    ru: {
        season: { yearRound: 'Круглый год' },
        months: ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'],
        facts: { region: 'Регион', whenToGo: 'Когда ехать', airport: 'Аэропорт', localTime: 'Местное время', km: 'км' },
        missing: { title: 'Страница города не найдена', back: 'Вернуться к турам' },
        crumbCities: 'Города',
        heroStay: (city) => `Найти жильё в ${city}`,
        heroTours: 'Выбрать экскурсию',
        anchor: { overview: 'О городе', see: 'Что посмотреть', map: 'Карта', services: 'Сервисы', hotels: 'Отели', tours: 'Экскурсии', faq: 'Вопросы', articles: 'Статьи', nearby: 'Рядом' },
        services: {
            esim: { ix: '// связь', title: 'eSIM', desc: 'Интернет без роуминга по прилёту.', go: 'Подключить' },
            transport: { ix: '// дорога', title: 'Транспорт', desc: 'Трансфер из аэропорта и поездки.', go: 'Найти' },
            stay: { ix: '// размещение', title: 'Жильё', desc: 'Отели и апартаменты в городе.', go: 'Смотреть' },
            tours: { ix: '// впечатления', title: 'Туры', desc: 'Экскурсии и морские прогулки.', go: 'Выбрать' },
        },
        overviewEyebrow: (city) => `${city}. О городе`,
        overviewTitle: 'Коротко о городе',
        seeEyebrow: (city) => `${city}. Что посмотреть`,
        seeTitle: 'Достопримечательности',
        seeAllLabel: 'Показать все',
        seeLead: 'Что обязательно увидеть в городе и окрестностях.',
        mapEyebrow: (city) => `${city}. Карта`,
        mapTitle: 'На карте',
        mapLead: 'Главные места и окрестности — чтобы спланировать прогулку и прикинуть расстояния.',
        servicesEyebrow: (city) => `${city}. Сервисы`,
        servicesTitle: 'Спланируйте поездку',
        servicesLead: 'Всё необходимое для города — связь, передвижение, жильё и экскурсии.',
        toursEyebrow: (city) => `${city}. Экскурсии`,
        toursTitle: 'Экскурсии и туры',
        toursAllLabel: 'Все экскурсии',
        toursLead: 'Морские прогулки, каньоны и пешие маршруты — что взять из города.',
        tourTag: 'Тур',
        tourView: 'Смотреть',
        faqEyebrow: (city) => `${city}. Вопросы и ответы`,
        faqLead: 'Ответы о сезоне, маршрутах, районах и базовой подготовке к поездке.',
        articlesEyebrow: (city) => `${city}. Полезные материалы`,
        articlesTitle: 'Статьи',
        nearbyEyebrow: (city) => `${city}. Рядом`,
        nearbyTitleAll: 'Все города страны',
        nearbyTitle: 'Города рядом',
        nearbyAllLabel: 'Все города',
        nearbyLeadAll: 'Все города страны — выберите следующее направление для поездки.',
        nearbyLead: 'Города поблизости — удобно добавить в маршрут и съездить на день.',
        loading: 'Загрузка…',
        nearbyOnly: 'Только ближайшие',
        showAll: 'Показать все города',
        ctaEyebrow: 'Спланируйте поездку за несколько минут',
        ctaTitle: (city) => `${city} — всё необходимое для путешествия.`,
        ctaConnectivity: 'Связь →',
        ctaStay: 'Жильё →',
        ctaTours: 'Экскурсии →',
    },
};
